import { useEffect, useRef } from "react";
import { Markdown } from "@altimateai/lego";
import { useStreaming } from "../useStreaming";
import { StreamStatus } from "../types";
import { generateStream } from "@/helpers/api_helper";
import styles from "./StreamingContent.module.scss";
import { RelativeComponentLoader } from "@/components/loader";

interface StreamingContentProps {
  id: string;
  endpoint: string;
  requestBody: Record<string, unknown> | null | undefined;
}

const pattern = /```[^`]*```|\n\n/gi;
const replaceNewlines = (value: string): string =>
  value.replace(pattern, match => (match.startsWith("```") ? match : " \n \n"));

export const StreamingContent: React.FC<StreamingContentProps> = ({
  id,
  endpoint,
  requestBody,
}) => {
  const { getStreamStatus, getStreamContent, updateStreamStatus, setStreamData, appendContent } =
    useStreaming();

  const abortControllerRef = useRef<AbortController | null>(null);
  const currentStatus = getStreamStatus(id);
  const content = getStreamContent(id);

  useEffect(() => {
    // If already finished or currently loading/ongoing, don't start again
    if (
      currentStatus === StreamStatus.Finished ||
      currentStatus === StreamStatus.Loading ||
      currentStatus === StreamStatus.Ongoing
    ) {
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const startStreaming = async () => {
      // Set both status and content together
      setStreamData(id, {
        status: StreamStatus.Loading,
        content: "",
      });

      let isFirstChunk = true;
      const onProgress = (chunk: string) => {
        // Update status on first chunk
        if (isFirstChunk && chunk.length > 0) {
          isFirstChunk = false;
          updateStreamStatus(id, StreamStatus.Ongoing);
        }
        appendContent(id, chunk);
      };

      try {
        await generateStream(endpoint, onProgress, requestBody, controller);
        updateStreamStatus(id, StreamStatus.Finished);
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          setStreamData(id, {
            status: StreamStatus.Error,
            content: error.message || "An error occurred",
          });
        }
      }
    };

    startStreaming();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, endpoint]); // Re-run if id or endpoint changes

  if (currentStatus === StreamStatus.Loading) {
    return <RelativeComponentLoader componentHeight={40} label="Gathering detailed insights..." />;
  }

  if (currentStatus === StreamStatus.Error) {
    return (
      <div className="al-text-red-500 al-p-4">
        <p>Error loading content:</p>
        <p className="al-mt-2">{content}</p>
      </div>
    );
  }

  return (
    <div className={`al-prose al-prose-sm al-max-w-none ${styles.streamingContent}`}>
      <Markdown content={replaceNewlines(content)} />
    </div>
  );
};

export default StreamingContent;
